import React, { useState } from "react";
import { Card, Table, Space, Row, Col, Tooltip, message } from "antd";
import moment from "moment";
import {
  TableColumnTitle,
  PrimaryTableRowText,
  TooltipText,
} from "../../../shared/globalStyling/styledText";
import Button from "../../../shared/globalStyling/Button";
import MediaFetcher from "../library/MediaFetcher";
import { useRemoveAdUnitsFromGroup } from "../../Creatives/hooks/useRemoveAdUnitsFromGroup";
import DownloadAdUnitButton from "./DownloadAdUnitButton";
import EditDisplayAdUnitsModal from "./EditDisplayAdUnitsModal";

const AdGroupMediaCard = ({
  mediaType = "DISPLAY", // "DISPLAY" or "VIDEO"
  orgMedias,
  adgroupinfo,
  refetchAdGroupInfo,
  adGroupId,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { removeAdUnits } = useRemoveAdUnitsFromGroup();
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Dynamic labels based on media type
  const isVideo = mediaType === "VIDEO";
  const unitLabel = isVideo ? "VIDEO AD UNITS IN GROUP" : "DISPLAY AD UNITS IN GROUP";
  const buttonLabel = isVideo ? "Add Video Ad Units" : "Add Display Ad Units";
  const modalTitle = isVideo ? "Edit Video Ad Units" : "Edit Display Ad Units";

  // Row selection for delete adunit
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: record => ({
      disabled: false,
      name: record.name,
    }),
  };

  // Row selection for download adunit
  const selectedKeys =
    adgroupinfo &&
    adgroupinfo.adGroupCreatives &&
    adgroupinfo.adGroupCreatives.medias
      ? adgroupinfo.adGroupCreatives.medias
          .filter(media => selectedRowKeys.includes(media.id))
          .map(media => media.key)
      : [];

  return (
    <Card
      title={
        <Space direction="vertical" style={{ width: "100%" }}>
          <span>
            {adgroupinfo &&
              adgroupinfo.adGroupCreatives &&
              adgroupinfo.adGroupCreatives.medias &&
              adgroupinfo.adGroupCreatives.medias.length}{" "}
            {unitLabel}
          </span>
          <Space
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Space>
              <Button
                type="default"
                danger
                disabled={selectedRowKeys.length === 0}
                onClick={async () => {
                  try {
                    await removeAdUnits(
                      adgroupinfo.adGroupCreatives,
                      selectedRowKeys
                    );
                    setSelectedRowKeys([]);
                    await refetchAdGroupInfo();
                    message.success(`Successfully removed ${selectedRowKeys.length} ad unit(s) from the group`);
                  } catch (error) {
                    message.error('Failed to remove ad units. Please try again.');
                  }
                }}
              >
                Remove
              </Button>
              <DownloadAdUnitButton
                creativeKeys={selectedKeys}
                disabled={selectedRowKeys.length === 0}
              />
            </Space>
            <Space>
              <Button
                type="default"
                onClick={() => setEditModalVisible(true)}
              >
                {buttonLabel}
              </Button>
            </Space>
          </Space>
        </Space>
      }
      size="default"
      style={{ width: "100%" }}
    >
      <div>
        <Table
          size="small"
          rowSelection={rowSelection}
          pagination={false}
          scroll={{ y: 400 }}
          rowKey="id"
          columns={[
            {
              title: <TableColumnTitle text={"Preview"} />,
              dataIndex: "preview",
              key: "preview",
              width: "15%",
              align: "left",
              render: (_, record) => (
                <div style={{ width: "80px", height: "80px" }}>
                  <MediaFetcher
                    mediaKey={record.key}
                    width="80px"
                    height="80px"
                    objectFit="contain"
                  />
                </div>
              ),
            },
            {
              title: <TableColumnTitle text={"Name"} />,
              dataIndex: "name",
              key: "name",
              width: "25%",
              align: "left",
              sorter: (a, b) => a.name.localeCompare(b.name),
              render: text => (
                <Row
                  type="flex"
                  justify="start"
                  align="middle"
                  style={{
                    width: "100%",
                  }}
                >
                  <Col span={24}>
                    {text.length > 46 ? (
                      <Tooltip title={<TooltipText text={text} />}>
                        <span>
                          <PrimaryTableRowText
                            text={text.slice(0, 46).concat("...")}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <PrimaryTableRowText text={text} />
                    )}
                  </Col>
                </Row>
              ),
            },
            {
              title: <TableColumnTitle text={"Aspect"} />,
              dataIndex: "aspect",
              key: "aspect",
              width: "10%",
              align: "left",
              sorter: (a, b) => a.width - b.width || a.height - b.height,
              render: (_, record) => (
                <div>
                  <PrimaryTableRowText
                    text={`${record.width}x${record.height}`}
                  />
                </div>
              ),
            },
            {
              title: <TableColumnTitle text={"File Type"} />,
              dataIndex: "fileType",
              key: "fileType",
              width: "10%",
              align: "left",
              sorter: (a, b) => a.fileType.localeCompare(b.fileType),
              render: fileType => <PrimaryTableRowText text={fileType} />,
            },
            {
              title: <TableColumnTitle text={"Date Uploaded"} />,
              dataIndex: "createdAt",
              key: "createdAt",
              width: "15%",
              align: "left",
              sorter: (a, b) => {
                const dateA = moment(a.createdAt);
                const dateB = moment(b.createdAt);
                return dateA.isBefore(dateB)
                  ? -1
                  : dateA.isAfter(dateB)
                  ? 1
                  : 0;
              },
              render: createdAt => {
                return (
                  <PrimaryTableRowText
                    text={moment(createdAt).format("MM/DD/YYYY hh:mm A")}
                  />
                );
              },
            },
          ]}
          bordered={false}
          dataSource={
            adgroupinfo &&
            adgroupinfo.adGroupCreatives &&
            adgroupinfo.adGroupCreatives.medias
              ? adgroupinfo.adGroupCreatives.medias
              : []
          }
        />
      </div>
      <EditDisplayAdUnitsModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        adGroupId={adGroupId}
        orgMedias={orgMedias || []}
        title={modalTitle}
        initialSelected={
          adgroupinfo &&
          adgroupinfo.adGroupCreatives &&
          adgroupinfo.adGroupCreatives.medias
            ? adgroupinfo.adGroupCreatives.medias.map(m => m.id)
            : []
        }
        onSubmit={refetchAdGroupInfo}
      />
    </Card>
  );
};

export default AdGroupMediaCard;